import {
    type ServiceDefinition,
    type ServiceParameter,
    type BoundParameterValue,
    type ParameterValueKind,
    type PriceCalculationValueWithUnit,
    type PriceCalculationParameterInfo,
} from "../api";
import { ValueObject } from "../base/ValueObject";

/**
 * Represents a base class for a value of parameters.
 *
 * This class implements the common interface for all parameter values.
 * It provides a base implementation for the methods that are common to all parameter values.
 */
class BaseParameterValue<
    VALUE = unknown,
    PARAMETER_VALUE extends BoundParameterValue<VALUE> = BoundParameterValue<VALUE>,
> {
    value?: VALUE;
    readonly parameterId: ServiceParameter.Id;
    readonly kind: ParameterValueKind;

    constructor(options: PARAMETER_VALUE) {
        this.kind = options.kind;
        this.parameterId = options.parameterId;
        this.value = options.value as VALUE;
    }
}

export abstract class ResolvedParameterValue<
        VALUE = unknown,
        PARAMETER_VALUE extends BoundParameterValue<VALUE> = BoundParameterValue<VALUE>,
    >
    extends ValueObject(BaseParameterValue)
    implements BoundParameterValue<VALUE>
{
    readonly combineWithParameters?: PriceCalculationParameterInfo.Id[];

    declare value: VALUE;

    constructor(options: PARAMETER_VALUE) {
        super(options);
        this.combineWithParameters = options.combineWithParameters;
    }

    getParameterId(): ServiceParameter.Id {
        return this.parameterId;
    }

    getValue<RESULT = VALUE>(): RESULT | undefined {
        return this.value as RESULT | undefined;
    }

    setValue(value: VALUE): this {
        this.value = value;
        return this;
    }

    getCombineWithParameters(): PriceCalculationParameterInfo.Id[] | undefined {
        return this.combineWithParameters;
    }

    abstract getCalculationParameterParameterInfos(): PriceCalculationParameterInfo[];
    abstract getCalculationParameterValues(
        values: VALUE | undefined
    ): Record<PriceCalculationParameterInfo.Id, PriceCalculationValueWithUnit>;
    abstract getViewText(values: VALUE | undefined): string | undefined;

    abstract getKind(): ParameterValueKind;

    /**
     * Creates a customer-safe version of this parameter value.
     *
     * @param requestPricing - The pricing mode of the service definition
     * @returns For "show-price" and "show-disclosed-price": this instance (full data)
     *          For "show-no-price": a new instance with pricing data stripped, or undefined to filter out
     */
    abstract toCustomerValue(
        requestPricing: ServiceDefinition.RequestPricing
    ): ResolvedParameterValue<VALUE, PARAMETER_VALUE> | undefined;
}
